import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { MapPin, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { PageBack } from "@/components/PageBack";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  LocationDisclosureBody,
  fetchLocationConsent,
  isConsentCurrent,
  resetLocationConsentCache,
  saveLocationConsentDecision,
  withdrawLocationConsent,
  type ConsentRecord,
} from "@/lib/location-consent";
import { LOCATION_DISCLOSURE_VERSION } from "@/lib/location-disclosure";
import { getConsentRetention, setConsentRetention } from "@/lib/location.functions";

export const Route = createFileRoute("/_authenticated/settings/location-privacy")({
  component: LocationPrivacyPage,
  head: () => ({
    meta: [
      { title: "Location privacy — MissionReady" },
      {
        name: "description",
        content:
          "Review the MissionReady location disclosure, see your acknowledgment history, and withdraw consent at any time.",
      },
    ],
  }),
});

function LocationPrivacyPage() {
  const { currentOrg, currentRole } = useAuth();
  const [record, setRecord] = useState<ConsentRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [retention, setRetention] = useState<string>("");
  const [savingRetention, setSavingRetention] = useState(false);
  const isAdmin = currentRole === "org_owner" || currentRole === "admin";

  const load = async (orgId: string) => {
    setLoading(true);
    setRecord(await fetchLocationConsent(orgId));
    setLoading(false);
  };

  useEffect(() => {
    if (!currentOrg) return;
    void load(currentOrg.id);
  }, [currentOrg?.id]);

  useEffect(() => {
    if (!currentOrg || !isAdmin) return;
    getConsentRetention({ data: { orgId: currentOrg.id } })
      .then((r: any) => setRetention(r?.retention_months ? String(r.retention_months) : ""))
      .catch(() => setRetention(""));
  }, [currentOrg?.id, isAdmin]);

  const accepted = isConsentCurrent(record);

  const agree = async () => {
    if (!currentOrg) return;
    setBusy(true);
    try {
      await saveLocationConsentDecision(currentOrg.id, true);
      toast.success("Location disclosure acknowledged");
      await load(currentOrg.id);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not save that");
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!currentOrg) return;
    setBusy(true);
    try {
      await withdrawLocationConsent(currentOrg.id);
      resetLocationConsentCache(currentOrg.id);
      toast.success("Consent withdrawn — new records will be filed without a location");
      await load(currentOrg.id);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not withdraw");
    } finally {
      setBusy(false);
    }
  };

  const saveRetention = async () => {
    if (!currentOrg) return;
    const trimmed = retention.trim();
    const months = trimmed === "" ? null : Number(trimmed);
    if (months !== null && (!Number.isInteger(months) || months < 1 || months > 600)) {
      toast.error("Enter a whole number of months between 1 and 600, or leave it blank");
      return;
    }
    setSavingRetention(true);
    try {
      await setConsentRetention({ data: { orgId: currentOrg.id, retentionMonths: months } });
      toast.success(months ? `Consent records kept for ${months} months` : "Consent records kept indefinitely");
    } catch (e: any) {
      toast.error(e?.message ?? "Only owners and admins can change this");
    } finally {
      setSavingRetention(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-8">
      <PageBack fallbackTo="/settings" label="Settings" />
      <h1 className="font-display text-2xl font-bold md:text-3xl">Location privacy</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        MissionReady records your location only when you file work records. It never follows you in
        the background.
      </p>

      <div className="mt-6 rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-2">
          {accepted ? (
            <ShieldCheck className="h-4 w-4 text-primary" />
          ) : (
            <ShieldOff className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="font-medium">
            {loading
              ? "Checking your acknowledgment…"
              : accepted
                ? "Acknowledged"
                : "Not acknowledged"}
          </span>
          {!loading && record?.accepted_at && !record.revoked_at && (
            <span className="text-xs text-muted-foreground">
              on {new Date(record.accepted_at).toLocaleString()} (version {record.disclosure_version})
            </span>
          )}
          {!loading && record?.revoked_at && (
            <span className="text-xs text-muted-foreground">
              withdrawn {new Date(record.revoked_at).toLocaleString()}
            </span>
          )}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Current disclosure version: {LOCATION_DISCLOSURE_VERSION}. Withdrawing stops future
          capture; records already filed keep the location they were filed with.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {!accepted && (
            <Button size="sm" disabled={busy || loading} onClick={() => void agree()}>
              <MapPin className="mr-1 h-4 w-4" /> I agree
            </Button>
          )}
          {accepted && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void withdraw()}>
              Withdraw consent
            </Button>
          )}
        </div>
      </div>

      <h2 className="mt-8 font-display text-lg font-semibold">The disclosure</h2>
      <div className="mt-2">
        <LocationDisclosureBody className="max-h-none space-y-4 rounded-lg border border-border bg-muted/30 p-4 text-sm" />
      </div>

      {isAdmin && (
        <div className="mt-8 rounded-xl border border-border bg-card p-4">
          <h2 className="font-display text-lg font-semibold">Consent record retention</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            How long acknowledgment records are kept for this workspace. Leave blank to keep them
            indefinitely until your legal review sets a period.
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="w-40">
              <Label htmlFor="retention-months" className="text-xs">
                Months
              </Label>
              <Input
                id="retention-months"
                inputMode="numeric"
                placeholder="Indefinite"
                value={retention}
                onChange={(e) => setRetention(e.target.value)}
              />
            </div>
            <Button size="sm" disabled={savingRetention} onClick={() => void saveRetention()}>
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
